// Adaptive polling: when the backend keeps returning "nothing changed", widen
// the gap between sync requests so an idle POS open all day stops hammering the
// edge function. Any change (or user activity) resets the caller's tick counter
// back to 0, which snaps the delay back to the base interval.
export const SYNC_BASE_MS = 30000;
export const SYNC_MAX_MS = 120000;
const SYNC_BACKOFF_FACTOR = 1.5;

export const nextSyncDelay = (emptyTicks = 0) => {
  const ticks = Number(emptyTicks);
  if (!Number.isFinite(ticks) || ticks <= 1) return SYNC_BASE_MS;
  const scaled = SYNC_BASE_MS * SYNC_BACKOFF_FACTOR ** (ticks - 1);
  return Math.min(Math.round(scaled), SYNC_MAX_MS);
};

// How far behind the tick's start we rewind the `updatedSince` watermark.
// Two things make an exact watermark lossy:
//   1. `updatedSince` is stamped from the POS device's clock but compared against
//      `last_modified` values written by other devices' clocks. A tablet running
//      a minute fast would silently skip a minute of orders, forever.
//   2. A row can commit with a `last_modified` just below the watermark but land
//      in the table after the query that carried it ran.
// Re-asking for the last 60s each tick costs a handful of duplicate rows the
// merge already deduplicates, and it is the difference between "an order was
// delayed" and "an order never arrived".
export const SYNC_WATERMARK_SLOP_MS = 60000;

// The ISO instant to send as `updatedSince` on the NEXT tick.
// Prefers a server-supplied clock (`serverTime` on the bootstrap bundle) because
// that shares a time base with the `last_modified` column; falls back to the
// device clock. Either way it is rewound by the slop above.
export const resolveSyncWatermark = ({ bundle, tickStartIso, slopMs = SYNC_WATERMARK_SLOP_MS } = {}) => {
  const serverTime = bundle?.serverTime || bundle?.server_time || null;
  const baseMs = [serverTime, tickStartIso].reduce((chosen, candidate) => {
    if (chosen !== null) return chosen;
    const parsed = new Date(candidate || '').getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }, null);

  if (baseMs === null) return null;
  return new Date(baseMs - Math.max(Number(slopMs) || 0, 0)).toISOString();
};

// How many rows a bootstrap bundle actually carried. Drives the adaptive
// backoff: zero means nothing changed anywhere, so the next poll can wait longer.
export const countBundleChanges = (bundle) => {
  if (!bundle || typeof bundle !== 'object') return 0;
  return ['orders', 'expenses', 'inventory'].reduce(
    (total, key) => total + (Array.isArray(bundle[key]) ? bundle[key].length : 0),
    0,
  );
};

// Failed-write retry backoff. A flat retry interval turns a sick connection
// into a self-inflicted traffic burst (the POS device once produced 87
// requests in 30s hammering a backend that couldn't receive POST bodies), so
// consecutive failed retry rounds back off exponentially with jitter and a
// cap. The attempt counter resets as soon as any sync succeeds.
export const RETRY_BASE_MS = 5000;
export const RETRY_MAX_MS = 120000;
const RETRY_BACKOFF_FACTOR = 2;
const RETRY_JITTER_RATIO = 0.2;

export const nextRetryDelay = (attempt = 0, random = Math.random) => {
  const step = Number.isFinite(Number(attempt)) ? Math.max(Number(attempt), 0) : 0;
  const scaled = Math.min(RETRY_BASE_MS * RETRY_BACKOFF_FACTOR ** step, RETRY_MAX_MS);
  // Jitter of ±20% so several stuck devices don't retry in lockstep.
  const jitter = scaled * RETRY_JITTER_RATIO * (random() * 2 - 1);
  return Math.round(scaled + jitter);
};

// The stored total of an order arriving from the cloud.
//
// This used to read `record.total || record.payment_amount`, which quietly
// repriced any zero-total order to whatever the gateway charged — Paystack's
// fee included — and the POS then wrote that figure back to the database as the
// order's price. Analytics already falls back to paymentAmount when it needs a
// revenue figure (see getOrderRevenueAmount), so the fallback belongs there, at
// the reporting layer, and never here where it becomes a permanent edit.
export const resolveCloudOrderTotal = (record) => {
  const total = Number(record?.total);
  return Number.isFinite(total) ? total : 0;
};

// Orders saved on this device that the cloud has never received and that are
// not waiting in the upload queue either.
//
// An upload rejected as "session expired" used to log the user out without
// queueing the order, so it stayed on the device forever while the header
// happily said "Synced". This finds those strays so they can be uploaded.
//
// Deliberately narrow, because an upload is an upsert that can overwrite:
//   - an order holding a supabase_id was uploaded once already, so its absence
//     from a fetch is not proof it is missing;
//   - website orders are born in the cloud and are never this device's to write;
//   - anything older than the age cap is left alone, so a tablet that sat in a
//     drawer cannot resurrect orders an admin has since removed.
export const STRANDED_ORDER_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

export const findStrandedOrders = ({
  localOrders = [],
  cloudOrders = [],
  pendingOrders = [],
  now = Date.now(),
  maxAgeMs = STRANDED_ORDER_MAX_AGE_MS,
} = {}) => {
  const idsOf = (orders) => (Array.isArray(orders) ? orders : [])
    .flatMap((order) => [order?.id, order?.supabase_id])
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map(String);
  const known = new Set([...idsOf(cloudOrders), ...idsOf(pendingOrders)]);

  return (Array.isArray(localOrders) ? localOrders : []).filter((order) => {
    if (!order || order.id === null || order.id === undefined || order.id === '') return false;
    if (order.supabase_id) return false;
    if (order.orderSource === 'website') return false;
    if (known.has(String(order.id))) return false;
    const placedAt = Date.parse(order.timestamp);
    if (!Number.isFinite(placedAt)) return false;
    return now - placedAt <= maxAgeMs;
  });
};

// A 403 means the signed-in login may not write this record (an expense queued
// by someone with My Expenses, stock changed by a cashier, or an amendment only
// its supervisor may upload). Retrying under the same login gets the same
// answer, so it is held for a login that can upload it instead of being shown
// as a sync error on every retry.
export const isRefusedForThisLogin = (error) => Number(error?.status) === 403;

// Login used to download the entire order history, 1,000 rows a request with a
// 15s limit on each, so one slow page on tablet Wi-Fi failed the whole sync.
// The queue, kitchen and stranded-order check only need recent orders; History
// and Analytics fetch the range they show. Seven days stays clear of the
// stranded-order age cap above, which needs the cloud copies of those orders.
export const LOGIN_ORDER_WINDOW_DAYS = 7;

export const loginOrderWindowStart = (now = new Date()) => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (LOGIN_ORDER_WINDOW_DAYS - 1));
  const pad = (value) => String(value).padStart(2, '0');
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
};

// Which stretch of order history this device has fully downloaded:
// { startMs, syncedAt } with startMs = -Infinity for all of it, or null. A range
// inside it only needs the rows changed since syncedAt; one reaching further
// back must be downloaded whole. Range dates parse the way the backend parses
// them, and endDate is inclusive (the backend adds a day).
const DAY_MS = 24 * 60 * 60 * 1000;

const rangeStartMs = (range) => {
  const parsed = Date.parse(range?.startDate || '');
  return Number.isFinite(parsed) ? parsed : -Infinity;
};

const rangeEndMs = (range) => {
  const parsed = Date.parse(range?.endDate || '');
  return Number.isFinite(parsed) ? parsed + DAY_MS : Infinity;
};

// The updatedSince to send for this range, or null to download it whole.
export const planOrderRangeFetch = ({ range = {}, coverage = null, slopMs = SYNC_WATERMARK_SLOP_MS } = {}) => {
  const syncedAtMs = Date.parse(coverage?.syncedAt || '');
  if (!coverage || !Number.isFinite(syncedAtMs)) return null;
  if (rangeStartMs(range) < coverage.startMs) return null;
  return new Date(syncedAtMs - Math.max(Number(slopMs) || 0, 0)).toISOString();
};

// The coverage after a successful fetch of `range` that started at fetchStartedAt.
export const nextOrderCoverage = ({ range = {}, coverage = null, fetchStartedAt, incremental = false } = {}) => {
  const start = rangeStartMs(range);
  const end = rangeEndMs(range);
  const reachesNow = end >= Date.parse(fetchStartedAt);

  // A change check only re-read rows inside the range, so it vouches for the
  // whole history only when the range spans all of it.
  if (incremental) {
    return coverage && start <= coverage.startMs && reachesNow ? { ...coverage, syncedAt: fetchStartedAt } : coverage;
  }

  if (!coverage) return reachesNow ? { startMs: start, syncedAt: fetchStartedAt } : null;
  // Disjoint from what we hold: leave a gap rather than claim it is covered.
  if (end < coverage.startMs) return coverage;

  const refreshedAll = start <= coverage.startMs && reachesNow;
  return {
    startMs: Math.min(coverage.startMs, start),
    syncedAt: refreshedAll ? fetchStartedAt : coverage.syncedAt,
  };
};
